import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Gift, ArrowRight, Sparkles } from 'lucide-react';

const GiftStrip = () => {
  const stripRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 }
    );
    const el = stripRef.current;
    if (el) observer.observe(el);
    return () => {
      if (el) observer.unobserve(el);
    };
  }, []);

  return (
    <section
      ref={stripRef}
      className="relative bg-gradient-to-r from-teal-700 via-teal-600 to-teal-700 overflow-hidden"
      data-testid="gift-strip"
    >
      {/* Decorative sparkle accents */}
      <Sparkles className="absolute top-4 left-8 w-4 h-4 text-white/20" aria-hidden="true" />
      <Sparkles className="absolute bottom-6 right-16 w-5 h-5 text-white/20" aria-hidden="true" />
      <Sparkles className="absolute top-1/2 left-1/3 w-3 h-3 text-white/15" aria-hidden="true" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 md:py-6">
        <div
          className={`flex flex-col md:flex-row md:items-center md:justify-between gap-4 transition-all duration-700 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'
          }`}
        >
          {/* Left: icon + copy */}
          <div className="flex items-start md:items-center gap-4 min-w-0">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center ring-2 ring-white/25">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-teal-100 mb-1">
                Give a Gift
              </div>
              <p className="text-white text-base md:text-lg font-serif leading-snug">
                Give someone an hour of quiet.{' '}
                <span className="text-teal-100 font-sans text-sm md:text-base">
                  Massage gift certificates — 30 / 60 / 90 minutes, coordinated by hand.
                </span>
              </p>
            </div>
          </div>

          {/* Right: price peek + CTA */}
          <div className="flex items-center gap-4 flex-shrink-0">
            <div className="hidden lg:flex items-center gap-3 text-teal-100 text-sm">
              <span data-testid="gift-strip-price-30">30 min · <span className="text-white font-medium">$55</span></span>
              <span className="text-white/30">|</span>
              <span data-testid="gift-strip-price-60">60 min · <span className="text-white font-medium">$110</span></span>
              <span className="text-white/30">|</span>
              <span data-testid="gift-strip-price-90">90 min · <span className="text-white font-medium">$160</span></span>
            </div>

            <Link
              to="/gift-certificates"
              className="group inline-flex items-center gap-2 bg-white text-teal-700 hover:bg-teal-50 px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-300 hover:shadow-xl hover:scale-105 whitespace-nowrap"
              data-testid="gift-strip-cta"
            >
              Send a gift
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GiftStrip;
